<template>
  <ErrorDialogBase :dialog="dialog" @dismiss-dialog="emit('dismissDialog')" @execute-callback="emit('executeCallback')">
    <template #title><v-card-title>{{ t('metadata-conversion-error-title') }}</v-card-title></template>
    <template #text>
        <TranslationComponent v-if="hasValidTranslation" :translation="t(messageKey)">
          <template #support-email-link>
            <AppLink :to="URL_MAILTO_SUPPORT">{{ EMAIL_SUPPORT }}</AppLink>
          </template>
          <template #status-page-link>
            <AppLink :to="URL_STATUS_PAGE">{{ URL_STATUS_PAGE }}</AppLink>
          </template>
        </TranslationComponent>
        <!-- Fallback to error's fallback message if translation fails -->
        <div v-else-if="originalError?.fallbackMessage">
          {{ originalError.fallbackMessage }}
        </div>
        <ErrorDialogRawMessage v-if="isAppError(dialog.error)" :error="dialog?.error"></ErrorDialogRawMessage>
    </template>
    <template #actions>
      <v-card-actions>
        <v-spacer/>
        <v-btn color="primary" @click="emit('dismissDialog')">{{ t('button-close') }}</v-btn>
        <v-btn color="primary" @click="emit('executeCallback')">{{ t('button-try-again') }}</v-btn>
      </v-card-actions>
    </template>
  </ErrorDialogBase>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {ErrorDialogEmits, ErrorDialogProps, isAppError} from "@/common/types";
import {useDialog} from "@/composables/useDialog";
import ErrorDialogBase from "./ErrorDialogBase.vue";
import {useTranslationNamespace} from "@/composables/useI18n";
import {I18nNamespace} from "@/i18n";
import {URL_MAILTO_SUPPORT, URL_STATUS_PAGE} from "@/constants/urls";
import {EMAIL_SUPPORT} from "@/constants/contact";
import {TranslationComponent} from "i18next-vue";
import AppLink from "@/components/common/AppLink.vue";
import ErrorDialogRawMessage from "./ErrorDialogRawMessage.vue";

const props = defineProps<ErrorDialogProps>()
const emit = defineEmits<ErrorDialogEmits>()

const t = useTranslationNamespace(I18nNamespace.ContentRegistration);

// Extract the original error from the enhanced error message
const extractOriginalError = (error: any) => {
  if (!error) return null;

  // If the error message starts with our prefix, extract the original message
  const prefix = 'XML to Form Data conversion failed: ';
  if (error.message && error.message.startsWith(prefix)) {
    // Create a synthetic error with the original message and preserved properties
    const originalMessage = error.message.substring(prefix.length);
    return {
      message: originalMessage,
      i18nKey: error.i18nKey,
      fallbackMessage: error.fallbackMessage
    };
  }

  return error;
};

// Check if error has a specific i18n key, otherwise use generic error
const getMessageKey = (error: any) => {
  const originalError = extractOriginalError(error);
  if (originalError?.i18nKey) {
    return originalError.i18nKey;
  }
  return 'xml_rendering_error_unknown';
};

const messageKey = getMessageKey(props.dialog?.error);

// Get the original error for fallback message handling
const originalError = extractOriginalError(props.dialog?.error);

// Check if translation exists (if translation equals key, it means translation failed)
const hasValidTranslation = computed(() => {
  const translation = t(messageKey);
  return translation !== messageKey && translation !== '';
});

const { getDialogTitle } = useDialog();
defineExpose({ isAppError });
</script>
